// The firmware facts for the device behind a unique number — small type, for
// the Message Box's centre pane (both shells), under the firmware picker.
//
//   Last code updated   5 Oct 2026, 18:39 · 2 h ago
//   Latest bin file     OtaForTuya.ino.bin           (what it downloads NOW)
//   Bin published       2 Oct 2026, 11:02 · 3 d ago
//   [ Older than the bin — update ]    update mark set · parked: UPDATE CODE
//
// Everything comes off the device's own detail row (GetDeviceDetailbyUniqueNumber,
// the device-level join) through firmwareStateOf — no extra request, and the
// same comparison the Firmware page makes, so the two cannot disagree.
//
// ⚠️ "LAST CODE UPDATED" IS AN ATTEMPT, NOT A SUCCESS. The firmware stamps
// CodeUpdated BEFORE it flashes (OtaForSwitching.ino:2097), so a failed or
// wrong-chip flash still moves it. The tooltip says so; "Up to date" here means
// "an update was started after this bin was published".

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Chip, Tooltip, Link, IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import {
  firmwareStateOf, parseServerTime, CURRENT, STALE,
} from '../../core/devices/firmwareInfo';
import { heartbeatOf } from '../../core/devices/firmwareFleet';
import { hasNoHeartbeat } from '../../core/devices/deviceStatus';
import { useDeviceStatus } from '../../core/mqtt/useDeviceStatus';

const when = (ms) => (ms == null ? '' : new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }));
const ago = (ms) => {
  if (ms == null) return '';
  const m = Math.max(0, Math.floor((Date.now() - ms) / 60000));
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
};
const secondsAgo = (ms) => {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  return s < 60 ? `${s} s ago` : `${Math.floor(s / 60)} min ago`;
};
const stamp = (ms, never) => (ms == null ? never : `${when(ms)} · ${ago(ms)}`);

const FS = 11;

function Row({ label, tip, children }) {
  const name = (
    <Typography component="span" sx={{ fontSize: FS, color: 'text.secondary', whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
  );
  return (
    <>
      {tip ? <Tooltip title={tip}>{name}</Tooltip> : name}
      <Typography component="span" sx={{ fontSize: FS, fontWeight: 600, minWidth: 0, wordBreak: 'break-all' }}>
        {children}
      </Typography>
    </>
  );
}

/**
 * @param device       the console's device row (GetDeviceDetailbyUniqueNumber)
 * @param refreshedAt  ms the row was last read, for the "live · read 4 s ago" line
 * @param onRefresh    re-read now
 * @param onUpdate     optional — when given, "Older than the bin — update" is a
 *                     BUTTON that opens Update Code for this device
 */
export default function FirmwareFacts({
  device, refreshedAt = null, onRefresh, onUpdate, heardAt = null,
}) {
  // ⚠️ "LAST SEEN" IS ALSO WHAT WE HEARD (2026-10-17). The detail reply's only
  // heartbeat used to be LastMqttPing, which moves on a status CHANGE — 803213
  // answered s05 and getallwifi in the Message Box while this read "3 d ago".
  // The server now adds NetworkLastUpdatedDateTime (preferred by heartbeatOf),
  // and a message this app hears from the device — live here (useDeviceStatus,
  // reference-counted) or the caller's log (`heardAt`, epoch ms) — counts too.
  const [, , liveAt] = useDeviceStatus(device ? (device.UniqueNumber ?? device.uniqueNumber) : null);
  // Re-render every few seconds so "read 4 s ago" counts, and "11 min ago" on
  // the dates above keeps moving between reads.
  const [, setNow] = useState(0);
  useEffect(() => {
    if (refreshedAt == null) return undefined;
    const t = setInterval(() => setNow((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, [refreshedAt]);

  if (!device) return null;
  // ⚠️ A BLE LAMP GROUP HAS NO WI-FI: no heartbeat, no firmware. "Last seen"
  // here read the minutes since it was CREATED (same bug as the room badge —
  // deviceStatus.hasNoHeartbeat). Say what it is instead.
  if (hasNoHeartbeat(device)) {
    return (
      <Typography variant="body2" sx={{ my: 0.5, opacity: 0.8 }}>
        Bluetooth device — it has no Wi-Fi, so there is no &quot;last seen&quot; and no firmware to update.
        Its own screen shows when it was last used, and by whom.
      </Typography>
    );
  }
  const f = firmwareStateOf(device);
  // eslint-disable-next-line no-nested-ternary
  const chip = f.state === CURRENT
    ? { color: 'success', label: f.withinTolerance ? 'Up to date (within 5 min of the build)' : 'Up to date' }
    : (f.state === STALE
      ? { color: 'warning', label: 'Older than the bin — update' }
      : { color: 'default', label: 'Unknown — no date on one side' });

  return (
    <Box sx={{ my: 0.5 }}>
      <Box sx={{
        display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.125, alignItems: 'baseline',
      }}
      >
        {(() => {
          const server = parseServerTime(heartbeatOf(device));
          const heard = Math.max(Number(liveAt) || 0, Number(heardAt) || 0) || null;
          const useHeard = heard != null && (server == null || heard > server);
          return (
            <Row
              label="Last seen"
              tip={useHeard
                ? 'A message from the device reached this app at this time (Message Box / live).'
                : "The device's last network ping to the server (NetworkConnected), else its last status report (LastMqttPing). Corrected for the backend's IST-as-UTC timestamps."}
            >
              {stamp(useHeard ? heard : server, 'never heard')}
              {useHeard ? ' · heard live' : ''}
            </Row>
          );
        })()}
        <Row
          label="Last code updated"
          tip="When the device last STARTED an update (DevicesInstalled.CodeUpdated). It is stamped before flashing, so a failed or wrong-chip flash still moves it."
        >
          {stamp(f.flashedAt, 'never recorded')}
        </Row>
        <Row label="Latest bin file" tip="What this device downloads if updated now — its own file, else its type's.">
          {f.binFile
            // The TEXT colour, underlined: the theme blue is the company ButtonColor,
            // and on a blue background (Smartify) it all but disappears.
            ? <Link href={f.binUrl} target="_blank" rel="noreferrer" underline="always" color="inherit" sx={{ fontSize: FS }}>{f.binFile}</Link>
            : 'none set'}
        </Row>
        <Row label="Bin published" tip="OTABinFile.HexFileDateTime — when that file was uploaded (about 5 min after it was built).">
          {stamp(f.builtAt, 'no date on the file')}
        </Row>
      </Box>
      <Box sx={{
        display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', mt: 0.375,
      }}
      >
        {f.state === STALE && onUpdate ? (
          <Tooltip title="Update this device's code now">
            <Chip
              size="small" color="warning" clickable onClick={onUpdate}
              icon={<SystemUpdateAltIcon />}
              label="Older than the bin — update code"
              sx={{ height: 20, fontSize: 10, fontWeight: 700, '& .MuiChip-icon': { fontSize: 13 } }}
            />
          </Tooltip>
        ) : (
          <Chip size="small" color={chip.color} label={chip.label} sx={{ height: 18, fontSize: 10 }} />
        )}
        {f.updateMarked && (
          <Chip size="small" color="warning" variant="outlined" label="update mark set" sx={{ height: 18, fontSize: 10 }} />
        )}
        {f.pendingCommand && (
          <Chip size="small" variant="outlined" label={`parked: ${f.pendingCommand}`} sx={{ height: 18, fontSize: 10 }} />
        )}
        {/* How fresh these facts are. The row re-reads itself when the device
            talks and every 30 s (useConsoleDevice); ⟳ forces one now. */}
        {refreshedAt != null && (
          <Typography component="span" sx={{ fontSize: 10, color: 'text.secondary', ml: 'auto', whiteSpace: 'nowrap' }}>
            {`live · read ${secondsAgo(refreshedAt)}`}
            {onRefresh && (
              <Tooltip title="Read the device row from the server now">
                <IconButton size="small" onClick={onRefresh} sx={{ p: 0.25, ml: 0.25, color: 'inherit' }}>
                  <RefreshIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            )}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
